<!--
  Forbrukerrådet - Kommunetesten
  @link https://gitlab01.copyleft.no/FBR/kommunetesten-new
  @author Anders Evenrud <anders.evenrud@copyleft.no>
-->
<template>
  <div class="table" :class="className">
    <table>
      <thead>
        <tr>
          <th
            v-for="(col, index) in columns"
            :key="index"
            :class="headerClass(index, col)"
            :aria-sort="sortAria(index)"
            @click="onSort($event, index, col.key)"
            >
            <div class="order">
              <span class="table__header--positionprefix" v-if="col.class === 'position'"># i</span>
              <span>{{ col.label }}</span>
              <font-awesome-icon :icon="sortIcon(index)" v-if="sortEnable(index)" />
            </div>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="row in paginatedRows" :key="row.kid" :class="rowClass(row)" @click="onClick($event, row)">
          <td class="table__position" v-if="!!county">{{ row.countyPosition }}</td>
          <td class="table__position">{{ row.countryPosition }}</td>

          <td class="table__name">
            <div class="description">
              <Shield :municipality="row.kid" :county="row.fid" :title="row.name" />
              <router-link :to="municipalityLink(row.kid, { year })">{{ row.name }}</router-link>
            </div>
          </td>

          <td class="table__score">{{ decimalLabel(row.score) }}</td>
        </tr>
      </tbody>
    </table>

    <button class="table__loadmore" @click="onLoadMore" v-if="hasMorePages">
      <div>
        <font-awesome-icon icon="sync" />
        <span>Se alle</span>
      </div>
    </button>
  </div>
</template>

<style lang="scss" scoped>
@import "../assets/variables";

.table {
  overflow: auto;

  table {
    width: 100%;
    //table-layout: fixed;
    border-collapse: collapse;
    font-size: inherit;
    border: 1px solid $list-border-color;
  }

  thead {
    background-color: $list-header-background;

    span {
      margin-right: 0.1em;
    }
  }

  tbody {
    background-color: $list-primary-background;

    tr:nth-child(2n+2) {
      background-color: $list-secondary-background;
    }
  }

  .table__row {
    cursor: pointer;

    &--active {
      background-color: $list-active-background !important;
    }
  }

  .table__header {
    &--active {
      font-weight: bold;
    }
  }

  .table__position {
    text-align: center;
    width: 3.5em;
  }

  .table__name {
    border-right: 0 none;
  }

  .table__score {
    border-left: 0 none;
    text-align: right;

    & > div {
      justify-content: flex-end;
    }
  }

  &--infinite {
    .table__loadmore > div {
      display: none;
    }
  }

  th,
  td {
    margin: 0;
    padding: 1em;
    border: 1px solid $list-border-color;
    text-align: left;
    line-height: normal;
    white-space: nowrap;

    & > div {
      display: flex;
      align-items: center;
      user-select: none;

      &.order {
        svg {
          margin-left: 0.5em;
          width: 0.5em;
        }
      }

      &.description {
        a {
          margin-left: 1em;
          text-decoration: underline !important;
        }
      }
    }
  }

  th {
    border-color: $list-header-border-color;
    font-weight: inherit;
    color: $link-text-color;
  }

  th {
    cursor: pointer;
  }

  &__loadmore {
    /* Reset button styles */
    margin: 0;
    padding: 0;
    -webkit-appearance: none;
    -moz-appearance: none;
    border: 0 none;
    width: 100%;
    text-align: left;
    font: inherit;
    background: transparent;

    & > div {
      padding: 1.5em;
      border: 1px solid $list-border-color;
      border-top: 0 none;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      span {
        padding-left: 0.5em;
      }
    }
  }
}

@media (max-width: $responsive-break) {
  .table {
    &__position {
      width: 3em;
    }

    &__score {
      display: none !important;
    }

    &__header {
      &--positionprefix {
        display: none;
      }
    }
  }
}
</style>

<script>
import Shield from '../components/Shield.vue'
import { sortBy, reverse } from 'lodash-es'

export default {
  components: {
    Shield
  },

  props: {
    active: Number,
    year: Number,
    rows: Array,
    county: Object,
    infiniteScroll: {
      type: Boolean,
      default: true
    },
    rowsPerPage: {
      type: Number,
      default: 20
    }
  },

  data () {
    return {
      observer: null,
      sortColumn: 0,
      sortDirection: 'asc',
      pageNumber: 1
    }
  },

  computed: {
    className () {
      return this.infiniteScroll && this.pageNumber > 1
        ? 'table--infinite'
        : 'table--regular'
    },

    sortedRows () {
      const { key } = this.columns[this.sortColumn]
      const results = sortBy(this.rows, (row, index) => key ? row[key] : index)

      return this.sortDirection === 'desc' ? reverse(results) : results
    },

    paginatedRows () {
      const list = this.sortedRows

      if (this.pageNumber > 1) {
        return list
      }

      return list
        .slice(0, Math.min(list.length, this.pageNumber * this.rowsPerPage))
    },

    columns () {
      const items = [{
        label: 'Norge',
        key: 'countryPosition',
        class: 'position'
      }, {
        label: 'Kommune',
        key: 'name',
        class: 'name'
      }, {
        label: 'Poeng',
        key: 'score',
        class: 'score'
      }]

      return this.county ? [{
        label: 'fylket',
        key: 'countyPosition',
        class: 'position'
      }, ...items] : items
    },

    hasMorePages () {
      return this.pageNumber * this.rowsPerPage < this.sortedRows.length
    }
  },

  methods: {
    onLoadMore () {
      this.pageNumber = this.pageNumber + 1
    },

    onClick (ev, row) {
      this.$emit('click', ev, row)
    },

    onSort (ev, index) {
      this.sortDirection = this.sortColumn === index
        ? this.sortDirection === 'asc' ? 'desc' : 'asc'
        : 'asc'

      this.sortColumn = index
    },

    sortIcon (index) {
      return 'sort'
      /*
      return this.sortDirection === 'asc'
        ? 'angle-down'
        : 'angle-up'
        */
    },

    sortAria (index) {
      return this.sortColumn === index
        ? `${this.sortDirection}ending`
        : 'none'
    },

    sortEnable (index) {
      return true
      // return this.sortColumn === index
    },

    headerClass (index, col) {
      return 'table__' + col.class + ' table__header--' + (this.sortColumn === index
        ? 'active'
        : 'inactive')
    },

    rowClass (row) {
      return 'table__row' + (this.active === row.kid
        ? '--active'
        : '')
    }
  }

  /*
  mounted () {
    const bottom = this.$el.querySelector('.table__loadmore')
    if (!bottom) {
      console.warn('bottom not found')
      return
    }

    const inner = bottom.querySelector('div')

    const cb = () => {
      if (!inner.offsetParent && this.infiniteScroll) {
        this.onLoadMore()
      }
    }

    this.observer = new IntersectionObserver(cb, {
      threshold: 0.25
    })

    this.observer.observe(bottom)
  },

  destroyed () {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
    */
}
</script>
